import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Webhook, Plus, Trash2, Send, CheckCircle, XCircle, Settings, Database, Server, RefreshCw, Link2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function ITSMOrchestration() {
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'integrations';
  const [integrations, setIntegrations] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isTicketDialogOpen, setIsTicketDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newIntegration, setNewIntegration] = useState({
    name: '',
    integration_type: 'jira',
    webhook_url: '',
    api_key: '',
    project_key: ''
  });
  
  const [newTicket, setNewTicket] = useState({
    integration_id: '',
    vulnerability_id: '',
    title: '',
    description: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [integrationsRes, ticketsRes, vulnsRes] = await Promise.all([
        api.get('/itsm/integrations'),
        api.get('/itsm/tickets'),
        api.get('/vulnerabilities')
      ]);
      setIntegrations(integrationsRes.data);
      setTickets(ticketsRes.data);
      setVulnerabilities(vulnsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIntegration = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/itsm/integrations', newIntegration);
      toast.success('Integration created successfully');
      setIsAddDialogOpen(false);
      setNewIntegration({ name: '', integration_type: 'jira', webhook_url: '', api_key: '', project_key: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create integration');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteIntegration = async (id) => {
    try {
      await api.delete(`/itsm/integrations/${id}`);
      toast.success('Integration deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete integration');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await api.post('/itsm/create-ticket', newTicket);
      toast.success(`Ticket ${response.data.ticket_id} created`);
      setIsTicketDialogOpen(false);
      setNewTicket({ integration_id: '', vulnerability_id: '', title: '', description: '', priority: 'medium' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const getIntegrationIcon = (type) => {
    return type === 'jira' ? '🔵' : type === 'servicenow' ? '🟢' : '⚪';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-xl">Loading ITSM data...</div>
      </div>
    );
  }

  return (
    <div data-testid="itsm-container" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-inter font-bold text-4xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            ITSM Orchestration
          </h1>
          <p className={`font-inter mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Jira & ServiceNow integration for security ticket management</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={isTicketDialogOpen} onOpenChange={setIsTicketDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-500" disabled={integrations.length === 0}>
                <Send className="w-4 h-4 mr-2" />
                Create Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className={`${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
              <DialogHeader>
                <DialogTitle className="font-inter text-2xl">Create ITSM Ticket</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTicket} className="space-y-4 mt-4">
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Integration</Label>
                  <Select 
                    value={newTicket.integration_id} 
                    onValueChange={(v) => setNewTicket({...newTicket, integration_id: v})}
                  >
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                      <SelectValue placeholder="Select integration" />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                      {integrations.map(integ => (
                        <SelectItem key={integ.id} value={integ.id}>
                          {getIntegrationIcon(integ.integration_type)} {integ.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Related Vulnerability (Optional)</Label>
                  <Select 
                    value={newTicket.vulnerability_id} 
                    onValueChange={(v) => setNewTicket({...newTicket, vulnerability_id: v})}
                  >
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                      <SelectValue placeholder="Link to vulnerability" />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                      <SelectItem value="">None</SelectItem>
                      {vulnerabilities.map(vuln => (
                        <SelectItem key={vuln.id} value={vuln.id}>
                          {vuln.title} ({vuln.severity})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Title</Label>
                  <Input
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                    placeholder="Security issue title"
                    required
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Description</Label>
                  <textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                    className={`w-full rounded-md p-3 mt-2 min-h-[100px] ${isDarkMode ? 'bg-zinc-950/50 border border-white/10 text-white' : 'bg-white border border-slate-300 text-slate-900'}`}
                    placeholder="Detailed description..."
                    required
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Priority</Label>
                  <Select 
                    value={newTicket.priority} 
                    onValueChange={(v) => setNewTicket({...newTicket, priority: v})}
                  >
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Ticket'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-500">
                <Plus className="w-4 h-4 mr-2" />
                Add Integration
              </Button>
            </DialogTrigger>
            <DialogContent className={`${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
              <DialogHeader>
                <DialogTitle className="font-inter text-2xl">Add ITSM Integration</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateIntegration} className="space-y-4 mt-4">
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Name</Label>
                  <Input
                    value={newIntegration.name}
                    onChange={(e) => setNewIntegration({...newIntegration, name: e.target.value})}
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                    placeholder="Production Jira"
                    required
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Integration Type</Label>
                  <Select 
                    value={newIntegration.integration_type} 
                    onValueChange={(v) => setNewIntegration({...newIntegration, integration_type: v})}
                  >
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}>
                      <SelectItem value="jira">Jira</SelectItem>
                      <SelectItem value="servicenow">ServiceNow</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Webhook URL</Label>
                  <Input
                    value={newIntegration.webhook_url}
                    onChange={(e) => setNewIntegration({...newIntegration, webhook_url: e.target.value})}
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                    placeholder="https://your-instance.atlassian.net/..."
                    required
                  />
                </div>
                {newIntegration.integration_type === 'jira' && (
                  <div>
                    <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Project Key</Label>
                    <Input
                      value={newIntegration.project_key}
                      onChange={(e) => setNewIntegration({...newIntegration, project_key: e.target.value})}
                      className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                      placeholder="SEC"
                    />
                  </div>
                )}
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>API Key (Optional)</Label>
                  <Input
                    type="password"
                    value={newIntegration.api_key}
                    onChange={(e) => setNewIntegration({...newIntegration, api_key: e.target.value})}
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                    placeholder="••••••••"
                  />
                </div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Integration'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={tabFromUrl} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
        <TabsList className={`${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
          <TabsTrigger value="integrations" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400">
            <Webhook className="w-4 h-4 mr-2" />
            Integrations ({integrations.length})
          </TabsTrigger>
          <TabsTrigger value="tickets" className="data-[state=active]:bg-emerald-600/20 data-[state=active]:text-emerald-400">
            <Send className="w-4 h-4 mr-2" />
            Tickets ({tickets.length})
          </TabsTrigger>
          <TabsTrigger value="cmdb" className="data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400">
            <Database className="w-4 h-4 mr-2" />
            CMDB/GRC
          </TabsTrigger>
        </TabsList>

        <TabsContent value="integrations" className="mt-5">
          {integrations.length === 0 ? (
            <div className={`rounded-2xl p-12 text-center ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
              <Webhook className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
              <h3 className={`text-xl font-inter font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No Integrations</h3>
              <p className={`mb-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Add Jira or ServiceNow integration to create security tickets</p>
              <Button onClick={() => setIsAddDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-500">
                <Plus className="w-4 h-4 mr-2" />
                Add First Integration
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {integrations.map((integ) => (
                <div key={integ.id} className={`rounded-xl p-5 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${
                        integ.integration_type === 'jira' 
                          ? isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'
                          : isDarkMode ? 'bg-green-500/20' : 'bg-green-100'
                      }`}>
                        {getIntegrationIcon(integ.integration_type)}
                      </div>
                      <div>
                        <h3 className={`font-inter font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{integ.name}</h3>
                        <p className={`text-xs capitalize ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{integ.integration_type}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteIntegration(integ.id)}
                      className={isDarkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-100'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Status</span>
                      <span className={`flex items-center gap-1 ${integ.is_active ? 'text-green-500' : 'text-red-500'}`}>
                        {integ.is_active ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {integ.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {integ.project_key && (
                      <div className="flex items-center justify-between">
                        <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Project</span>
                        <span className={`font-mono ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{integ.project_key}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className={isDarkMode ? 'text-zinc-500' : 'text-slate-500'}>Webhook</span>
                      <span className={`text-xs truncate max-w-[150px] ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{integ.webhook_url}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tickets" className="mt-5">
          <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
            <div className="scroll-container">
              <table className="w-full min-w-[600px]">
              <thead className={isDarkMode ? 'bg-zinc-900/70' : 'bg-slate-50'}>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-inter font-semibold uppercase ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Ticket ID</th>
                  <th className={`px-4 py-3 text-left text-xs font-inter font-semibold uppercase ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Title</th>
                  <th className={`px-4 py-3 text-left text-xs font-inter font-semibold uppercase ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Status</th>
                  <th className={`px-4 py-3 text-left text-xs font-inter font-semibold uppercase ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Created</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-white/5' : 'divide-slate-200'}`}>
                {tickets.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-12 text-center">
                      <Send className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                      <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>No tickets created yet</p>
                    </td>
                  </tr>
                ) : (
                  tickets.map((ticket) => (
                    <tr key={ticket.id} className={isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}>
                      <td className="px-4 py-3">
                        <span className={`font-mono text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{ticket.external_ticket_id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{ticket.title}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          ticket.status === 'open' 
                            ? isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                            : ticket.status === 'closed' 
                            ? isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                            : isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {ticket.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cmdb" className="mt-5">
          <CMDBPanel isDarkMode={isDarkMode} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// CMDB/GRC Panel Component
function CMDBPanel({ isDarkMode }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [newConfig, setNewConfig] = useState({
    integration_type: 'servicenow',
    instance_url: '',
    auth_type: 'basic',
    sync_interval_minutes: 60,
    sync_assets: true,
    sync_vulnerabilities: true,
    sync_compliance: true
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await api.get('/integrations/cmdb/config');
      setConfigs(response.data.configurations || []);
    } catch (error) {
      console.error('Failed to fetch CMDB configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const configureIntegration = async () => {
    try {
      await api.post('/integrations/cmdb/configure', newConfig);
      toast.success('CMDB integration configured');
      setShowConfigModal(false);
      fetchConfigs();
    } catch (error) {
      toast.error('Failed to configure integration');
    }
  };

  const triggerSync = async (integrationType) => {
    setSyncing(integrationType);
    try {
      const response = await api.post(`/integrations/cmdb/${integrationType}/sync`);
      toast.success(`Sync completed: ${response.data.assets_synced} assets, ${response.data.vulnerabilities_synced} vulnerabilities`);
      fetchConfigs();
    } catch (error) {
      toast.error('Sync failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(null);
    }
  };

  const exportData = async (integrationType, dataType) => {
    try {
      const response = await api.get(`/integrations/cmdb/${integrationType}/export?data_type=${dataType}`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${integrationType}_${dataType}_export.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Exported ${response.data.record_count} ${dataType}`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return <div className="text-center py-8"><div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div></div>;
  }

  return (
    <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass border border-white/10' : 'bg-white border border-slate-200 shadow-card'}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`font-inter text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'} flex items-center gap-2`}>
            <Database className="w-5 h-5 text-blue-400" />
            CMDB/GRC Integrations
          </h3>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Sync with ServiceNow CMDB and RSA Archer GRC
          </p>
        </div>
        <Button onClick={() => setShowConfigModal(true)} className="bg-blue-600 hover:bg-blue-700">
          <Link2 className="w-4 h-4 mr-2" /> Add Integration
        </Button>
      </div>

      {configs.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
          <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No CMDB/GRC integrations configured</p>
          <p className="text-sm mt-1">Connect to ServiceNow or RSA Archer</p>
        </div>
      ) : (
        <div className="space-y-4">
          {configs.map((config) => (
            <div key={config.integration_type} className={`p-5 rounded-xl border ${isDarkMode ? 'bg-zinc-900/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.integration_type === 'servicenow' ? 'bg-green-500/20' : 'bg-purple-500/20'}`}>
                    <Server className={`w-6 h-6 ${config.integration_type === 'servicenow' ? 'text-green-400' : 'text-purple-400'}`} />
                  </div>
                  <div>
                    <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      {config.integration_type === 'servicenow' ? 'ServiceNow CMDB' : 'RSA Archer GRC'}
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>{config.instance_url}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`px-2 py-1 rounded text-xs ${config.enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                        {config.enabled ? 'Active' : 'Inactive'}
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                        Sync every {config.sync_interval_minutes} min
                      </span>
                      {config.last_sync && (
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                          Last: {new Date(config.last_sync).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 mt-2">
                      {config.sync_assets && <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">Assets</span>}
                      {config.sync_vulnerabilities && <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">Vulnerabilities</span>}
                      {config.sync_compliance && <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">Compliance</span>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => triggerSync(config.integration_type)}
                    disabled={syncing === config.integration_type}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {syncing === config.integration_type ? (
                      <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Syncing...</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-1" /> Sync Now</>
                    )}
                  </Button>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => exportData(config.integration_type, 'assets')} className={`text-xs ${isDarkMode ? 'border-white/10' : ''}`}>
                      <Download className="w-3 h-3 mr-1" /> Assets
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => exportData(config.integration_type, 'vulnerabilities')} className={`text-xs ${isDarkMode ? 'border-white/10' : ''}`}>
                      <Download className="w-3 h-3 mr-1" /> Vulns
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Configure Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
          <DialogHeader>
            <DialogTitle>Configure CMDB/GRC Integration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Integration Type</label>
              <Select value={newConfig.integration_type} onValueChange={(v) => setNewConfig({...newConfig, integration_type: v})}>
                <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                  <SelectItem value="servicenow">ServiceNow CMDB</SelectItem>
                  <SelectItem value="rsa_archer">RSA Archer GRC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Instance URL</label>
              <Input
                value={newConfig.instance_url}
                onChange={(e) => setNewConfig({...newConfig, instance_url: e.target.value})}
                placeholder="https://company.service-now.com"
                className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Auth Type</label>
                <Select value={newConfig.auth_type} onValueChange={(v) => setNewConfig({...newConfig, auth_type: v})}>
                  <SelectTrigger className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                    <SelectItem value="basic">Basic Auth</SelectItem>
                    <SelectItem value="oauth">OAuth 2.0</SelectItem>
                    <SelectItem value="api_key">API Key</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Sync Interval (min)</label>
                <Input
                  type="number"
                  value={newConfig.sync_interval_minutes}
                  onChange={(e) => setNewConfig({...newConfig, sync_interval_minutes: parseInt(e.target.value)})}
                  className={isDarkMode ? 'bg-zinc-800 border-white/10' : ''}
                />
              </div>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={newConfig.sync_assets} onChange={(e) => setNewConfig({...newConfig, sync_assets: e.target.checked})} />
                <span className="text-sm">Sync Assets</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={newConfig.sync_vulnerabilities} onChange={(e) => setNewConfig({...newConfig, sync_vulnerabilities: e.target.checked})} />
                <span className="text-sm">Sync Vulnerabilities</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={newConfig.sync_compliance} onChange={(e) => setNewConfig({...newConfig, sync_compliance: e.target.checked})} />
                <span className="text-sm">Sync Compliance</span>
              </label>
            </div>
            <Button onClick={configureIntegration} className="w-full bg-blue-600 hover:bg-blue-700">
              Configure Integration
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
